import { useState } from "react";
import { useParams, Link } from "react-router-dom";
import {
  useGetChapterFirmApplicationDetailsQuery,
  useChapterAcceptFirmApplicationMutation,
  useChapterRejectFirmApplicationMutation,
} from "../api/firmApi";
import FirmStatusBadge from "../components/FirmStatusBadge";
import FirmPersonCard from "../components/FirmPersonCard";
import { firmStorageUrl } from "../utils";
import {
  Loader2, ArrowLeft, ShieldCheck, CheckCircle2, AlertCircle, FileText,
} from "lucide-react";

/**
 * One firm application, as its chapter sees it before endorsing — everything
 * the applicant declared, then Endorse / Reject. The firm counterpart of
 * ChapterUpgradeApplicationDetailPage.
 */

function safeMsg(x: any) {
  return String(x?.data?.message || x?.message || x?.error || "Something went wrong");
}

function Row({ label, value }: { label: string; value: any }) {
  if (value === null || value === undefined || value === "") return null;
  return (
    <div className="flex justify-between gap-4 py-1.5 text-sm border-b border-gray-50 last:border-0">
      <span className="text-gray-500">{label}</span>
      <span className="text-gray-900 text-right">{String(value)}</span>
    </div>
  );
}

export default function ChapterFirmApplicationDetailPage() {
  const { id } = useParams();
  const { data: app, isLoading, refetch } = useGetChapterFirmApplicationDetailsQuery(id!, { skip: !id });
  const [acceptApp, { isLoading: accepting }] = useChapterAcceptFirmApplicationMutation();
  const [rejectApp, { isLoading: rejecting }] = useChapterRejectFirmApplicationMutation();

  const [comment, setComment] = useState("");
  const [reason, setReason] = useState("");
  const [flashMsg, setFlashMsg] = useState<{ ok: boolean; text: string } | null>(null);

  if (isLoading) {
    return <div className="p-8 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-gray-400" /></div>;
  }
  if (!app) return <div className="p-8 text-center text-gray-400">Application not found.</div>;

  const flash = (ok: boolean, text: string) => {
    setFlashMsg({ ok, text });
    setTimeout(() => setFlashMsg(null), 4000);
  };

  const partners: any[] = app.partners ?? [];
  const endorsed = !!app.chapter_accepted_at;
  const canAct = ["awaiting_endorsement", "under_review"].includes(app.status) && !endorsed;

  const onAccept = async () => {
    if (!comment.trim()) return flash(false, "A comment is required to endorse.");
    try {
      const res: any = await acceptApp({ id: app.id, comment: comment.trim() }).unwrap();
      flash(true, res?.message ?? "Firm application endorsed.");
      setComment("");
      refetch();
    } catch (e) {
      flash(false, safeMsg(e));
    }
  };

  const onReject = async () => {
    try {
      const res: any = await rejectApp({ id: app.id, reason }).unwrap();
      flash(true, res?.message ?? "Firm application rejected.");
      setReason("");
      refetch();
    } catch (e) {
      flash(false, safeMsg(e));
    }
  };

  return (
    <div className="w-full p-4 md:p-6 max-w-3xl mx-auto">
      <Link to="/chapter/firm-applications" className="inline-flex items-center gap-1 text-sm text-indigo-600 hover:underline mb-4">
        <ArrowLeft className="w-4 h-4" /> Back to firm endorsements
      </Link>

      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{app.practice_name}</h1>
          <p className="text-xs text-gray-400">
            #{String(app.id).padStart(6, "0")} · applied by {app.user?.name}
          </p>
        </div>
        <FirmStatusBadge status={app.status} />
      </div>

      {flashMsg && (
        <div
          className={`mb-4 p-3 rounded-lg border text-sm flex items-center gap-2 ${
            flashMsg.ok
              ? "bg-green-50 border-green-200 text-green-800"
              : "bg-red-50 border-red-200 text-red-800"
          }`}
        >
          {flashMsg.ok ? <CheckCircle2 className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />}
          {flashMsg.text}
        </div>
      )}

      {endorsed && (
        <div className="mb-4 p-3 rounded-lg bg-green-50 border border-green-200 text-sm text-green-800 flex items-start gap-2">
          <ShieldCheck className="w-4 h-4 mt-0.5 shrink-0" />
          <span>
            Endorsed{app.chapter_accepted_by?.name ? ` by ${app.chapter_accepted_by.name}` : ""}.
            {app.chapter_accept_comment ? ` — ${app.chapter_accept_comment}` : ""}
          </span>
        </div>
      )}

      <div className="bg-white border rounded-xl p-5 mb-4">
        <h3 className="font-semibold text-gray-900 mb-2">Firm details</h3>
        <Row label="Nature of practice" value={app.nature_of_practice} />
        <Row label="Registered address" value={app.registered_address} />
        <Row label="Email" value={app.email} />
        <Row label="Phone" value={app.phone} />
        <Row label="CAC/CAMA number" value={app.cama_reg_number} />
        <Row label="Ownership structure" value={app.ownership_structure} />
        <Row label="Number of partners / directors" value={app.num_partners} />
        <Row label="Affiliations" value={(app.affiliations ?? []).join(", ") || null} />
      </div>

      <div className="bg-white border rounded-xl p-5 mb-4">
        <h3 className="font-semibold text-gray-900 mb-1">Partners &amp; directors</h3>
        {partners.length === 0 && <p className="text-sm text-gray-400 mt-2">None listed.</p>}
        {partners.map((p: any) => <FirmPersonCard key={p.id} p={p} />)}
      </div>

      {(app.benevolence_records ?? []).length > 0 && (
        <div className="bg-white border rounded-xl p-5 mb-4">
          <h3 className="font-semibold text-gray-900 mb-2">Acts of benevolence to NIQS</h3>
          {(app.benevolence_records ?? []).map((b: any) => (
            <div key={b.id} className="py-2 border-b last:border-0">
              {b.type && <p className="text-xs font-medium text-indigo-600">{b.type}</p>}
              <p className="text-sm text-gray-700 whitespace-pre-wrap">{b.description}</p>
            </div>
          ))}
        </div>
      )}

      <div className="bg-white border rounded-xl p-5 mb-4">
        <h3 className="font-semibold text-gray-900 mb-3">Referees</h3>
        {(app.referees ?? []).length === 0 && <p className="text-sm text-gray-400">None.</p>}
        {(app.referees ?? []).map((r: any) => (
          <div key={r.id} className="flex items-center justify-between py-2 border-b last:border-0">
            <div>
              <p className="text-sm text-gray-900">{r.member?.surname ?? r.member_id}</p>
              <p className="text-xs text-gray-400">{r.member?.membership_no}</p>
            </div>
            <span className="text-xs text-gray-500">{r.decision}</span>
          </div>
        ))}
      </div>

      <div className="bg-white border rounded-xl p-5 mb-4">
        <h3 className="font-semibold text-gray-900 mb-3">Registration documents</h3>
        {(app.documents ?? []).length === 0 && <p className="text-sm text-gray-400">None uploaded.</p>}
        {(app.documents ?? []).map((d: any) => {
          const url = firmStorageUrl(d.file_path);
          return (
            <div key={d.id} className="flex items-center justify-between py-2 border-b last:border-0">
              <div className="flex items-center gap-2 min-w-0">
                <FileText className="w-4 h-4 text-gray-400 shrink-0" />
                <div className="min-w-0">
                  <p className="text-sm text-gray-900">{d.doc_type}</p>
                  <p className="text-xs text-gray-400 truncate">{d.file_name}</p>
                </div>
              </div>
              {url && (
                <a href={url} target="_blank" rel="noreferrer" className="text-xs text-indigo-600 hover:underline shrink-0">
                  View
                </a>
              )}
            </div>
          );
        })}
      </div>

      {canAct && (
        <div className="bg-white border rounded-xl p-5 space-y-5">
          <h3 className="font-semibold text-gray-900">Chapter decision</h3>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Endorsement comment <span className="text-red-500">*</span>
            </label>
            <textarea
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-indigo-200"
              rows={3}
              placeholder="Your comment endorsing this practice…"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
            />
            <button
              onClick={onAccept}
              disabled={!comment.trim() || accepting}
              className="mt-2 px-5 py-2.5 rounded-lg bg-green-600 text-white text-sm font-medium hover:bg-green-700 disabled:opacity-60"
            >
              {accepting ? "Endorsing…" : "Endorse"}
            </button>
          </div>

          <div className="border-t pt-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">Reason for rejection</label>
            <textarea
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-red-200"
              rows={2}
              placeholder="Optional…"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
            <button
              onClick={onReject}
              disabled={rejecting}
              className="mt-2 px-5 py-2.5 rounded-lg border border-red-200 text-red-600 text-sm font-medium hover:bg-red-50 disabled:opacity-60"
            >
              {rejecting ? "Rejecting…" : "Reject"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
